<script setup>
// NOTICE: KAPTION-INTEGRATION
import { computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { emitter } from 'shared/helpers/mitt';
import KaptionCircleFlag from 'dashboard/components-next/flag/KaptionCircleFlag.vue';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';
import { useUserLanguage } from 'dashboard/composables/useUserLanguage';
import { useAccount } from 'dashboard/composables/useAccount';
// NOTICE: KAPTION-INTEGRATION - Use composable for consistent preferences + master toggle check
import { useKaptionAIFeaturesPreferences } from 'dashboard/composables/useKaptionAIFeaturesPreferences';
import { useUISettings } from 'dashboard/composables/useUISettings';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
} = useInbox();

const { userLanguage } = useUserLanguage();
const { account } = useAccount();
const { t } = useI18n();

// NOTICE: KAPTION-INTEGRATION - isBotPrivateNote is provided by Message.vue context
const {
  status,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
  conversationId,
  isBotPrivateNote,
} = useMessageContext();

const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm a', userLanguage?.value)
);

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  // All messages marked as delivered for the web widget inbox and API inbox once they are sent.
  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});

// NOTICE: KAPTION-INTEGRATION - Durable approval state for new-conversation
// template deliveries. new-conversation-modal.SEND.2
const kaptionDeliveryState = computed(
  () =>
    contentAttributes.value?.kaptionDeliveryState ||
    contentAttributes.value?.kaption_delivery_state
);
const kaptionDeliveryLabel = computed(() => {
  if (sourceId.value && kaptionDeliveryState.value === 'dispatching')
    return null;
  const labels = {
    pending_approval: t('NEW_CONVERSATION_MODAL.DELIVERY.PENDING_APPROVAL'),
    dispatching: t('NEW_CONVERSATION_MODAL.DELIVERY.SENDING'),
    cancelled: t('NEW_CONVERSATION_MODAL.DELIVERY.CANCELLED'),
    failed: t('NEW_CONVERSATION_MODAL.DELIVERY.TEMPLATE_UNAVAILABLE'),
  };
  return labels[kaptionDeliveryState.value];
});

// NOTICE: KAPTION-INTEGRATION
// Check if message has translation and show language label (using camelCase for frontend)
// Supports both new multi-language format and old single translation format
const kaptionTranslation = computed(() => {
  const attrs = contentAttributes.value;
  if (!attrs) return null;

  // Check new format first (multiple translations)
  const translations = attrs.kaptionTranslations;
  if (translations && typeof translations === 'object') {
    // Try user's language first
    if (userLanguage?.value && translations[userLanguage.value]) {
      return translations[userLanguage.value];
    }
    // Fallback to account locale
    const locale = account?.value?.locale;
    if (locale && typeof locale === 'string') {
      const accountLocale = locale.split('_')[0]?.toLowerCase?.();
      if (accountLocale && translations[accountLocale]) {
        return translations[accountLocale];
      }
    }
    // Fallback to first available translation
    const firstKey = Object.keys(translations)[0];
    if (firstKey && translations[firstKey]?.text) {
      return translations[firstKey];
    }
  }

  // Fallback to old format (single translation) for backwards compatibility
  return attrs.kaptionTranslation || null;
});

const hasKaptionTranslation = computed(() => {
  return kaptionTranslation.value?.text;
});

// NOTICE: KAPTION-INTEGRATION - Changed to use circle-flags SVG instead of emoji
const sourceLanguageCode = computed(() => {
  return kaptionTranslation.value?.sourceLanguage || null;
});

// NOTICE: KAPTION-INTEGRATION - Use composable for per-conversation preferences
// This ensures consistent localStorage keys across all components
const { translationEnabled, setTranslation } =
  useKaptionAIFeaturesPreferences(conversationId);

// NOTICE: KAPTION-INTEGRATION - Get master toggle from uiSettings (topbar setting)
const { uiSettings } = useUISettings();
const masterToggleEnabled = computed(
  () => uiSettings.value?.kaption_live_translation_enabled ?? true
);

// NOTICE: KAPTION-INTEGRATION - Combined check: master toggle AND per-conversation preference
// This ensures flag is hidden when the global toggle is OFF
const isTranslationEnabled = computed(() => {
  return masterToggleEnabled.value && translationEnabled.value;
});

// NOTICE: KAPTION-INTEGRATION - Listen for translation toggle events from KaptionAIFeaturesPill
// Update the composable's ref when the event fires to maintain reactivity
const handleTranslationToggle = ({
  conversationId: eventConversationId,
  enabled,
}) => {
  if (eventConversationId === conversationId.value) {
    setTranslation(enabled);
  }
};

onMounted(() => {
  emitter.on('kaption-translation-toggled', handleTranslationToggle);
});

onUnmounted(() => {
  emitter.off('kaption-translation-toggled', handleTranslationToggle);
});
</script>

<template>
  <div
    class="flex items-center gap-1.5"
    :class="[isBotPrivateNote ? 'text-[8px]' : 'text-xs']"
  >
    <div class="inline">
      <time class="inline">{{ readableTime }}</time>
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION - Show lock icon for regular private notes, no icon for bot notes -->
    <Icon
      v-if="isPrivate && !isBotPrivateNote"
      icon="i-lucide-lock-keyhole"
      class="size-3"
    />
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
    <span
      v-if="kaptionDeliveryLabel"
      class="inline-flex items-center gap-1 font-medium text-n-slate-10"
      data-testid="kaption-delivery-state"
    >
      <span class="i-lucide-clock-3 size-3" />
      {{ kaptionDeliveryLabel }}
    </span>
    <!-- NOTICE: KAPTION-INTEGRATION -->
    <!-- Show language flag if message is translated (using circle-flags SVG) -->
    <!-- Uses isTranslationEnabled which checks both master toggle AND per-conversation preference -->
    <KaptionCircleFlag
      v-if="hasKaptionTranslation && isTranslationEnabled && sourceLanguageCode"
      :language-code="sourceLanguageCode"
      size="size-3"
      :title="
        'Translated from ' +
        (kaptionTranslation?.sourceLanguage?.toUpperCase() || '')
      "
    />
  </div>
</template>
